/**
 * Domain hooks for the Tendly domain.
 *
 * Each hook encapsulates the factory's canonical queryKey + the Drizzle query builder:
 * consumers never call `createPowerSyncQuery`
 * directly for this data.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { AttachmentRecord, PowerSyncDrizzleDB } from "@tendly/powersync";

import {
  type AnnualCostRow,
  type AssetWithMeta,
  getAllServiceRecordsQuery,
  getAllUsageReadingsQuery,
  getAnnualCostsQuery,
  getAssetAttachmentsQuery,
  getAssetDetailQuery,
  getAssetsQuery,
  getReminderRulesQuery,
  getServiceAttachmentsQuery,
  getServiceRecordsQuery,
  getTrashedAssetsQuery,
  getUsageReadingsQuery,
  getWarrantiesQuery,
  queryKeys,
  type ReminderRuleWithAsset,
  type ServiceRecordLite,
  type ServiceRecordWithMeta,
  type UsageReadingLite,
  type UsageReadingWithMeta,
  type WarrantyWithAsset,
} from "../index";

import { createPowerSyncQuery, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

interface BaseTendlyQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

interface AssetScopedQueryOptions extends BaseTendlyQueryOptions {
  assetId: Accessor<string | undefined>;
}

// ─── Assets ──────────────────────────────────────────────────────────────────

export function useAssetsQuery(options: BaseTendlyQueryOptions) {
  return createPowerSyncQuery<AssetWithMeta>({
    queryKey: () => queryKeys.assets.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getAssetsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export function useAssetDetailQuery(options: AssetScopedQueryOptions) {
  return createPowerSyncQuery<AssetWithMeta>({
    queryKey: () => queryKeys.assets.detail(options.assetId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.assetId();
      return db && id ? getAssetDetailQuery(db, id) : null;
    },
    enabled: () => Boolean(options.assetId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Assets in the trash (soft-delete) — trash view. */
export function useTrashedAssetsQuery(options: BaseTendlyQueryOptions) {
  return createPowerSyncQuery<AssetWithMeta>({
    queryKey: () => queryKeys.assets.trashed(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getTrashedAssetsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Services ────────────────────────────────────────────────────────────────

export function useServiceRecordsQuery(options: AssetScopedQueryOptions) {
  return createPowerSyncQuery<ServiceRecordWithMeta>({
    queryKey: () => queryKeys.services.byAsset(options.assetId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.assetId();
      return db && id ? getServiceRecordsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.assetId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export function useAllServiceRecordsQuery(options: BaseTendlyQueryOptions) {
  return createPowerSyncQuery<ServiceRecordLite>({
    queryKey: () => queryKeys.services.allByUser(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getAllServiceRecordsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export function useAnnualCostsQuery(options: AssetScopedQueryOptions) {
  return createPowerSyncQuery<AnnualCostRow>({
    queryKey: () => queryKeys.services.annualCosts(options.assetId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.assetId();
      return db && id ? getAnnualCostsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.assetId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Readings ─────────────────────────────────────────────────────────────────

export function useUsageReadingsQuery(options: AssetScopedQueryOptions) {
  return createPowerSyncQuery<UsageReadingWithMeta>({
    queryKey: () => queryKeys.readings.byAsset(options.assetId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.assetId();
      return db && id ? getUsageReadingsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.assetId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export function useAllUsageReadingsQuery(options: BaseTendlyQueryOptions) {
  return createPowerSyncQuery<UsageReadingLite>({
    queryKey: () => queryKeys.readings.allByUser(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getAllUsageReadingsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Reminders ────────────────────────────────────────────────────────────────

export function useReminderRulesQuery(options: BaseTendlyQueryOptions) {
  return createPowerSyncQuery<ReminderRuleWithAsset>({
    queryKey: () => queryKeys.reminders.rules(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getReminderRulesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Warranties ────────────────────────────────────────────────────────────────

export function useWarrantiesQuery(options: BaseTendlyQueryOptions) {
  return createPowerSyncQuery<WarrantyWithAsset>({
    queryKey: () => queryKeys.warranties.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getWarrantiesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Documents ───────────────────────────────────────────────────────────────

export function useAssetAttachmentsQuery(options: AssetScopedQueryOptions) {
  return createPowerSyncQuery<AttachmentRecord>({
    queryKey: () => queryKeys.attachments.byAsset(options.assetId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.assetId();
      return db && id ? getAssetAttachmentsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.assetId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export function useServiceAttachmentsQuery(
  options: BaseTendlyQueryOptions & { serviceRecordId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<AttachmentRecord>({
    queryKey: () => queryKeys.attachments.byService(options.serviceRecordId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.serviceRecordId();
      return db && id ? getServiceAttachmentsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.serviceRecordId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
